import { clearCompanyLogoFile } from "./companyLogoStore";
import { clearAllVendorLogoFiles } from "./vendorLogoStore";

export type YesNo = "yes" | "no";

export type MonitoringId = "hourly" | "daily" | "weekly" | "monthly" | "manual";

export type NewCustomerWizardStep1Draft = {
  legalEntityName: string;
  tradeName: string;
  primaryWebsiteUrl: string;
  industry: string;
  companySize: string;
  hqCountry: string;
  timeZone: string;
  q1Payments: YesNo;
  q2Privacy: YesNo;
  q3Phi: YesNo;
  q4UaeIa: YesNo;
  /** Ids (compliance.standard_def.standard_id) the admin manually removed from the yes/no-derived
   * "Applicable standards" starting set. */
  removedStandardIds?: string[];
  /** Ids the admin manually added on top of the yes/no-derived starting set. */
  addedStandardIds?: string[];
  /** The fully-resolved final chip list at the time Step 1 was last saved — what actually gets
   * submitted as `step_1.standard_ids`. Recomputed from removed/added ids whenever this page is
   * revisited, so it always reflects the current yes/no answers + manual edits. */
  selectedStandardIds?: string[];
  planTier: string;
  endpointSeats: string;
  vendorSeats: string;
  contractStartDate: string;
  contractPeriod: string;
  renewalDate: string;
};

export type NewCustomerWizardStep3Draft = {
  fullName: string;
  clientEmail: string;
  roleTitle: string;
  phone: string;
};

export type NewCustomerWizardStep2Draft = {
  monitoring: MonitoringId;
  hourlyIntervalHours: string;
  hourlyStartTime: string;
  dailyTime: string;
  weeklyDay: string;
  weeklyTime: string;
  monthlyDay: string;
  monthlyTime: string;
  windowsCount?: string;
  macosCount?: string;
  linuxCount?: string;
  securityTools?: string[];
  cloudProviders?: string[];
  /** Free-text "Other" endpoint tools the admin added as chips. */
  securityToolsOther?: string[];
  /** Free-text "Other" cloud providers the admin added as chips. */
  cloudProvidersOther?: string[];
  /** In-progress Other composer (not yet added). */
  securityToolOtherDraft?: string;
  cloudOtherDraft?: string;
  vendors?: Array<{ organisation: string; url: string }>;
  /** In-progress "add vendor" composer fields — not yet added to `vendors`.
   * Saved too so half-typed vendor details survive a Back-then-forward round trip
   * even if the admin never clicked "+ Add vendor" before navigating away. */
  vendorOrgDraft?: string;
  vendorUrlDraft?: string;
};

export type NewCustomerWizardDraft = {
  step1?: NewCustomerWizardStep1Draft;
  step2?: NewCustomerWizardStep2Draft;
  step3?: NewCustomerWizardStep3Draft;
  /** Set after the first successful Save draft API call. */
  serverDraftId?: string;
};

export function getServerDraftId(): string | null {
  return getNewCustomerWizardDraft()?.serverDraftId?.trim() || null;
}

const STORAGE_KEY = "ns-new-customer-wizard-draft";

export function saveNewCustomerWizardDraft(draft: NewCustomerWizardDraft): void {
  try {
    const existing = getNewCustomerWizardDraft();
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...existing, ...draft }),
    );
  } catch {
    /* quota / private mode */
  }
}

export function getNewCustomerWizardDraft(): NewCustomerWizardDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as NewCustomerWizardDraft;
  } catch {
    return null;
  }
}

export function clearNewCustomerWizardDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
  clearCompanyLogoFile();
  clearAllVendorLogoFiles();
}
